import React, { createContext, useContext, useState, useEffect } from 'react';
import { UploadResult, UploadError } from '../types';

interface UploadState {
  status: 'idle' | 'loading' | 'error' | 'success';
  uploadResults: UploadResult | null;
  errors: UploadError[];
  uploadedFile: File | null;
}

interface UploadContextType {
  uploadState: UploadState;
  setUploadState: (state: Partial<UploadState>) => void;
  clearUploadState: () => void;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

const STORAGE_KEY = 'smartcheck_upload_state';

const initialState: UploadState = {
  status: 'idle',
  uploadResults: null,
  errors: [],
  uploadedFile: null,
};

export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [uploadState, setUploadStateInternal] = useState<UploadState>(initialState);

  // Lade den gespeicherten Zustand beim Start
  useEffect(() => {
    const savedState = localStorage.getItem(STORAGE_KEY);
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        // File-Objekt kann nicht serialisiert werden, also setzen wir es nur zurück wenn status nicht loading ist
        if (parsedState.status !== 'loading') {
          setUploadStateInternal({
            ...parsedState,
            uploadedFile: parsedState.uploadedFileName ? 
              new File([], parsedState.uploadedFileName) : null
          });
        }
      } catch (error) {
        console.error('Fehler beim Laden des Upload-Status:', error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  const setUploadState = (newState: Partial<UploadState>) => {
    setUploadStateInternal(prev => {
      const updatedState = { ...prev, ...newState };
      
      // Speichere im localStorage (außer bei loading status)
      if (updatedState.status !== 'loading') {
        const stateToSave = {
          ...updatedState,
          uploadedFileName: updatedState.uploadedFile?.name || null,
          uploadedFile: null // File-Objekt kann nicht serialisiert werden
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
      }
      
      return updatedState;
    });
  };

  const clearUploadState = () => {
    setUploadStateInternal(initialState);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <UploadContext.Provider value={{ uploadState, setUploadState, clearUploadState }}>
      {children}
    </UploadContext.Provider>
  );
};

export const useUpload = () => {
  const context = useContext(UploadContext);
  if (context === undefined) {
    throw new Error('useUpload must be used within an UploadProvider');
  }
  return context;
}; 